import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActions, siteNavItems } from "@/app/content";
import { GLOSSARY_GROUPS, GLOSSARY_TERMS, type GlossaryTerm } from "@/app/seo-glossary";

const groupAccents: Record<GlossaryTerm["group"], string> = {
  core: "#A3E635",
  role: "#3B82F6",
  marketplace: "#8B5CF6",
  engineering: "#14B8A6",
  console: "#F97316",
  "billing-governance": "#FACC15",
};

export default function GlossaryPage() {
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <ContentHero
        eyebrow="GLOSSARY · 术语表"
        eyebrowColor="#A3E635"
        titleTop="一份"
        titleBottom="不会漂移的术语表"
        bottomColor="#A3E635"
        lead="CALL ANYTHING 是一个让 AI Agent 通过标准化 Hotline 协议调用外部能力的开放体系。这页是站内、README、blog、第三方文章统一引用的术语标准；任何近义词都以这里的写法为准，不混用。"
        meta={[`${GLOSSARY_TERMS.length} 条术语`, "JSON-LD DefinedTermSet", "为 LLM 抓取优化"]}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Glossary" },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <nav
          className="mb-10 border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
          aria-label="术语分组"
        >
          <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">分组目录</p>
          <ul className="mt-4 flex flex-wrap gap-3">
            {GLOSSARY_GROUPS.map((group) => (
              <li key={group.id}>
                <a
                  href={`#group-${group.id}`}
                  className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 text-xs font-black uppercase text-black transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
                  style={{ boxShadow: `4px 4px 0 0 ${groupAccents[group.id]}` }}
                >
                  {group.label}
                  <span className="text-[10px] font-black text-zinc-500">
                    {GLOSSARY_TERMS.filter((t) => t.group === group.id).length}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="space-y-12">
          {GLOSSARY_GROUPS.map((group) => {
            const terms = GLOSSARY_TERMS.filter((t) => t.group === group.id);
            const accent = groupAccents[group.id];
            return (
              <section key={group.id} id={`group-${group.id}`} className="scroll-mt-24">
                <header className="border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                  <div className="flex items-center gap-3">
                    <span className="h-6 w-6 border-2 border-black" style={{ backgroundColor: accent }} aria-hidden />
                    <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">{group.label}</h2>
                  </div>
                  <p className="mt-3 text-sm font-bold leading-6 text-zinc-700 sm:text-base">
                    {group.intro}
                  </p>
                </header>

                <div className="mt-6 grid gap-5">
                  {terms.map((term) => (
                    <article
                      key={term.id}
                      id={term.id}
                      className="scroll-mt-24 border-4 border-black bg-white/85 backdrop-blur-sm p-5 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] sm:p-6"
                    >
                      <header className="flex flex-wrap items-baseline justify-between gap-3">
                        <div>
                          <h3 className="text-xl font-black uppercase text-black sm:text-2xl">{term.term}</h3>
                          {term.alias ? (
                            <p className="mt-1 text-sm font-bold text-zinc-500">{term.alias}</p>
                          ) : null}
                        </div>
                        <a
                          href={`#${term.id}`}
                          className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 hover:text-black"
                          aria-label={`链接锚点 ${term.term}`}
                        >
                          #{term.id}
                        </a>
                      </header>

                      <p className="mt-4 text-sm font-bold leading-7 text-zinc-800 sm:text-base">
                        {term.definition}
                      </p>

                      {term.context ? (
                        <p className="mt-3 border-l-4 border-black bg-[#F8F6EF] p-3 text-sm font-bold leading-6 text-zinc-700">
                          {term.context}
                        </p>
                      ) : null}

                      {term.relatedIds && term.relatedIds.length > 0 ? (
                        <p className="mt-4 text-xs font-black uppercase tracking-[0.18em] text-zinc-500">
                          相关：
                          {term.relatedIds.map((rid, i) => {
                            const rt = GLOSSARY_TERMS.find((x) => x.id === rid);
                            if (!rt) return null;
                            return (
                              <span key={rid}>
                                {i > 0 ? <span className="mx-1 text-zinc-400">·</span> : null}
                                <a href={`#${rt.id}`} className="text-black underline-offset-2 hover:underline">
                                  {rt.term}
                                </a>
                              </span>
                            );
                          })}
                        </p>
                      ) : null}
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <aside className="mt-14 border-[6px] border-black bg-[#A3E635]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <h2 className="text-2xl font-black uppercase text-black sm:text-3xl">下一步</h2>
          <p className="mt-3 text-sm font-bold leading-6 text-black sm:text-base">
            理解了术语后，最快的方式是直接开始。Responder 上手 5 分钟看到收入路径，Caller 上手 5 分钟看到 result_package。
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href="/docs/quick-start-responder"
              className="inline-flex items-center gap-2 border-4 border-black bg-black px-5 py-3 text-sm font-black uppercase text-white shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              开通我的 Hotline
            </a>
            <a
              href="/docs/quick-start-caller"
              className="inline-flex items-center gap-2 border-4 border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              接入到 Agent
            </a>
            <a
              href="/compare/hotline-vs-mcp/"
              className="inline-flex items-center gap-2 border-4 border-black bg-white px-5 py-3 text-sm font-black uppercase text-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5"
            >
              vs MCP / Skill / API
            </a>
          </div>
        </aside>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
